import { useState } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { CheckCircle, XCircle, Send, ShieldAlert } from 'lucide-react';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import { useHoneypot } from '../hooks/useHoneypot';
import { isValidName, isValidPhone } from '../lib/validate';

const inputCls = 'w-full px-4 py-3 rounded-control bg-white dark:bg-dark-surface border border-line dark:border-dark-line text-ink dark:text-dark-ink text-sm placeholder:text-ink-muted outline-none focus:border-primary transition-colors disabled:opacity-50';

const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-ink-soft dark:text-dark-ink-soft mb-2">
    {children}<span className="text-red-500 ml-0.5">*</span>
  </label>
);

const AccountDeletionRequest = ({ role }) => {
  const isDriver = role === 'driver';
  const roleLabel = isDriver ? 'Driver' : 'Rider';
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const { honeypotFieldProps, isSpam, spamCheckFields } = useHoneypot();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidName(formData.name)) {
      setFieldError('Enter a valid name');
      return;
    }
    if (!isValidPhone(formData.phone)) {
      setFieldError('Enter a valid phone number (10-15 digits)');
      return;
    }
    setFieldError(null);
    if (isSpam()) {
      setSubmitStatus('success');
      setFormData({ name: '', phone: '' });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/api/account-deletion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, role, ...spamCheckFields() }),
      });
      if (!res.ok) throw new Error(`submit failed: ${res.status}`);
      setSubmitStatus('success');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      console.error('Account deletion request failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title={`${roleLabel} Account Deletion Request | TotoWala`}
        description={`Request deletion of your TotoWala ${roleLabel.toLowerCase()} account and associated data.`}
        canonical={`/${isDriver ? 'drivers' : 'riders'}/account-deletion-request`}
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: `${roleLabel} Account Deletion Request`,
          url: `https://totowala.in/${isDriver ? 'drivers' : 'riders'}/account-deletion-request`,
        }}
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[560px] mx-auto text-center">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4">
              {roleLabel} Account Deletion Request
            </h1>
            <p className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed mb-2">
              Submit your name and registered phone number below to request permanent deletion of your TotoWala {roleLabel.toLowerCase()} account and associated data.
            </p>
            <p className="text-sm text-ink-muted leading-relaxed">
              Our team will verify your identity and process the request within 7 business days.
            </p>
          </div>
        </section>

        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[520px] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
              className="rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line p-6 sm:p-7"
            >
              {submitStatus === 'success' && (
                <div className="mb-5 px-4 py-3.5 bg-teal-tint dark:bg-teal/10 border border-teal/25 rounded-control flex items-center gap-3">
                  <CheckCircle size={18} className="text-teal-dark dark:text-teal shrink-0" />
                  <span className="text-sm text-teal-dark dark:text-teal">Request submitted. We'll confirm by phone within 7 business days.</span>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-5 px-4 py-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/25 rounded-control flex items-center gap-3">
                  <XCircle size={18} className="text-red-600 dark:text-red-400 shrink-0" />
                  <span className="text-sm text-red-700 dark:text-red-400">Failed to submit. Please email us directly at support@totowala.in</span>
                </div>
              )}
              {fieldError && (
                <div className="mb-5 px-4 py-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/25 rounded-control flex items-center gap-3">
                  <XCircle size={18} className="text-red-600 dark:text-red-400 shrink-0" />
                  <span className="text-sm text-red-700 dark:text-red-400">{fieldError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <input {...honeypotFieldProps} type="text" placeholder="Company" />
                <div>
                  <Label>Full Name</Label>
                  <input className={inputCls} type="text" name="name" value={formData.name} onChange={handleChange} required disabled={isSubmitting} placeholder="Your full name" />
                </div>
                <div>
                  <Label>Registered Phone Number</Label>
                  <input className={inputCls} type="tel" inputMode="tel" pattern="\+?[0-9]{10,15}" name="phone" value={formData.phone} onChange={handleChange} required disabled={isSubmitting} placeholder="+91 XXXXX XXXXX" />
                </div>

                <div className="flex items-start gap-2.5 px-4 py-3 rounded-control bg-surface-alt dark:bg-dark-surface-alt">
                  <ShieldAlert size={15} className="text-ink-muted shrink-0 mt-0.5" />
                  <span className="text-xs text-ink-muted leading-relaxed">
                    This deletes your {roleLabel.toLowerCase()} account and associated data{isDriver ? ', including earnings history' : ', including ride history'}. This cannot be undone.
                  </span>
                </div>

                <button
                  type="submit" disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-control bg-primary hover:bg-primary-dark text-white font-bold text-sm transition-colors disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Submitting...</>
                  ) : (
                    <><Send size={16} />Submit Deletion Request</>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default AccountDeletionRequest;
